/**
 * Per-medicine stock resolution for the dispense screens.
 *
 * Lifted verbatim out of `pages/pharmacy/StartDispensePage.js` so the OPD flow can
 * reuse it. Every function here is pure or takes plain arguments — no React state —
 * which is what makes it shareable.
 */
import { searchPrescribeAvailability } from '../api/pharmacy';
import { getItem, findInventoryItemByName, searchMedicineSources } from '../api/inventoryMaster';
import { resolveMedicineUomId } from './pharmacyMappers';
import { isControlledOrNarcotic } from './controlledDrug';
import { defaultUom } from './itemDisplay';
import { MEDICINE_ORIGIN, findMedicineMasterRow } from './newMedicineEntity';

export const STOCK_STATUS = {
  NOT_LISTED: 'NOT_LISTED',
  OUT_OF_STOCK: 'OUT_OF_STOCK',
  IN_STOCK: 'IN_STOCK',
};

export function hasMedicineStock(item) {
  if (!item?.item_id) return false;
  if (item.has_stock === false) return false;
  const qty = Number(item.available_quantity ?? item.stock) || 0;
  return qty > 0;
}

/** Only pass dosage_form when it looks like a form (Tablet, Syrup), not a full medicine name. */
export function normalizeDosageForm(value) {
  const trimmed = String(value || '').trim();
  if (!trimmed || trimmed.length > 30 || /\d\s*(mg|ml|g|mcg)\b/i.test(trimmed)) return '';
  return trimmed;
}

export async function fetchPrescribeSearch(params) {
  const result = await searchPrescribeAvailability(params).catch(() => ({ items: [] }));
  return result.items || [];
}

export function findBrandCatalogueMatch(items, medicineName) {
  if (!items.length || !medicineName) return null;
  const normalized = String(medicineName).toLowerCase().trim();
  return items.find((item) => {
    const name = String(item.item_name || item.name || '').toLowerCase().trim();
    const brand = String(item.brand_name || '').toLowerCase().trim();
    return name === normalized || brand === normalized
      || name.includes(normalized) || normalized.includes(name);
  }) || null;
}

export async function enrichItemUom(item) {
  if (!item?.item_id) return item;
  try {
    const master = await getItem(item.item_id);
    // Dispensing debits `uom_id` — it must prefer the item's issue UOM, not
    // silently collapse to base_uom_id (wrong whenever issue != base).
    // base_uom_id stays the item's real base, kept separate for any caller
    // that needs the base-unit truth (e.g. a future issue-vs-base hint).
    const issueUomId = item.uom_id || defaultUom(master, 'issue') || resolveMedicineUomId(master, master?.base_uom);
    const baseUomId = master?.base_uom_id || resolveMedicineUomId(master?.base_uom) || issueUomId;
    return {
      ...item,
      ...(issueUomId ? { uom_id: issueUomId } : {}),
      ...(baseUomId ? { base_uom_id: baseUomId } : {}),
      is_controlled_drug: master?.is_controlled_drug ?? item.is_controlled_drug,
      is_narcotic: master?.is_narcotic ?? item.is_narcotic,
      controlled: isControlledOrNarcotic(master) || isControlledOrNarcotic(item),
    };
  } catch {
    // submit validation will surface missing UOM
  }
  return item;
}

/**
 * Brand-name search only — generic substitute search is manual via the Substitute button.
 * `line` needs `{ id, medicine, route? }`; OPD callers pass the free-text prescribed name
 * as `medicine` and the ABDM `route` (which `normalizeDosageForm` discards unless it
 * really is a dosage form).
 */
export async function resolvePrescribeLineStock({ line, tenantId, hospitalId, storeId }) {
  const baseParams = {
    tenant_id: tenantId,
    hospital_id: hospitalId,
    location_id: storeId,
  };
  const dosageForm = normalizeDosageForm(line.route);
  const searchOpts = dosageForm ? { dosage_form: dosageForm } : {};

  const brandItems = await fetchPrescribeSearch({
    ...baseParams,
    q: line.medicine,
    ...searchOpts,
  });
  const brandMatch = findBrandCatalogueMatch(brandItems, line.medicine);

  if (brandMatch && hasMedicineStock(brandMatch)) {
    const enriched = await enrichItemUom(brandMatch);
    return {
      lineId: line.id,
      status: STOCK_STATUS.IN_STOCK,
      item: enriched,
      message: null,
    };
  }

  if (brandMatch) {
    const enrichedPrimary = await enrichItemUom(brandMatch);
    return {
      lineId: line.id,
      status: STOCK_STATUS.OUT_OF_STOCK,
      item: enrichedPrimary,
      message: `"${brandMatch.item_name || line.medicine}" is out of stock at this store. Click Substitute to search by generic name.`,
    };
  }

  return {
    lineId: line.id,
    status: STOCK_STATUS.NOT_LISTED,
    item: null,
    message: `"${line.medicine}" is not listed in the pharmacy catalogue. Click Substitute to search by generic name.`,
  };
}

/**
 * For a NOT_LISTED line: does an item of this name already exist, and if not, what
 * does main_medicine know about it? See newMedicineEntity for the rules.
 *
 * The item lookup deliberately drops prescribe/search's group and store filters,
 * so an item hidden from this store still counts as existing. The medicine-master
 * lookup starts at the same time, because search_medicines always also waits on the
 * ABDM registry. It only pre-fills the form, so its failure just means an emptier
 * form. This never rejects: a failed item lookup comes back as FAILED, and FAILED
 * never offers Create.
 */
export async function resolveNewMedicineOrigin(name) {
  const medicine = String(name || '').trim();
  const masterRows = searchMedicineSources(medicine).catch(() => []);
  let item;
  try {
    item = await findInventoryItemByName(medicine);
  } catch {
    return { status: MEDICINE_ORIGIN.FAILED, item: null, masterRow: null };
  }
  if (item) return { status: MEDICINE_ORIGIN.IN_INVENTORY, item, masterRow: null };
  return {
    status: MEDICINE_ORIGIN.NOT_IN_INVENTORY,
    item: null,
    masterRow: findMedicineMasterRow(await masterRows, medicine),
  };
}

export function applyMatchedItemToLine(row, match) {
  if (!match) return row;
  return {
    ...row,
    item_id: match.item_id || row.item_id,
    uom_id: match.uom_id || match.base_uom_id || row.uom_id || '',
    // Everything the screen needs to label, cap and price a quantity in the
    // item's issue unit — resolved by the catalogue, never derived here.
    issue_uom_id: match.issue_uom_id || match.uom_id || '',
    issue_uom_code: match.issue_uom_code || '',
    issue_uom_factor: match.issue_uom_factor ?? null,
    base_uom_code: match.base_uom_code || '',
    // Per ISSUE unit, to match the quantity the pharmacist enters.
    unit_rate: row.unit_rate || match.rate_issue_uom || match.mrp_issue_uom || '',
    batch_number: row.batch_number || (match.batch_number || ''),
    is_controlled_drug: match.is_controlled_drug ?? row.is_controlled_drug,
    is_narcotic: match.is_narcotic ?? row.is_narcotic,
    controlled: isControlledOrNarcotic(match) || isControlledOrNarcotic(row),
  };
}
